import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import ImageIcon from "@mui/icons-material/Image";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Titles from "../titles";
import {
  GetLibraryCall,
  AddLibraryReferenceCall,
  RemoveLibraryReferenceCall,
  GetTitlesCallNoStore,
  AddSubTitlesCall,
  GetLibraryVisibilityCall,
  UpdateLibraryVisibilityCall,
} from "../../redux/api/title.api";

const TABS = [
  { id: "his", labelKey: "pages.myLibrary.histology", icon: ImageIcon },
  { id: "emb", labelKey: "pages.myLibrary.embryology", icon: ImageIcon },
  { id: "three_d_model", labelKey: "pages.myLibrary.models3d", icon: ViewInArIcon },
];

function MyLibraryPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const authId = useSelector((state) => state?.auth?.id);
  const lang = useSelector((state) => state?.general?.lang) || "en";
  const [tabIndex, setTabIndex] = useState(0);
  const currentTab = TABS[tabIndex];
  const tabCategory = currentTab?.id || "his";

  const [libraryData, setLibraryData] = useState({ private: [], referenced: [] });
  const [browseOpen, setBrowseOpen] = useState(false);
  const [publicTitlesForTab, setPublicTitlesForTab] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [expandedTitleIds, setExpandedTitleIds] = useState({});
  const [targetSubtitleId, setTargetSubtitleId] = useState(null);
  const [targetTitleId, setTargetTitleId] = useState(null);
  const [browseSearchQuery, setBrowseSearchQuery] = useState("");
  const [visibilityByCategory, setVisibilityByCategory] = useState({});

  const loadLibrary = async () => {
    const res = await GetLibraryCall(lang);
    setLibraryData({
      private: res?.private || [],
      referenced: res?.referenced || [],
    });
  };

  const loadVisibility = async () => {
    const vis = await GetLibraryVisibilityCall();
    const raw = vis || {};
    // Backend uses "published" for "only my private slides"; frontend dropdown uses "private"
    const mapBackendToFrontend = (v) => (v === "published" ? "private" : v);
    setVisibilityByCategory({
      his: mapBackendToFrontend(raw.his),
      emb: mapBackendToFrontend(raw.emb),
      three_d_model: mapBackendToFrontend(raw.three_d_model),
    });
  };

  useEffect(() => {
    if (!authId) return;
    loadLibrary();
    loadVisibility();
  }, [authId, lang]);

  const privateForTab = (libraryData.private || []).filter((i) => {
    const category = (i.library_category || "").toString().toLowerCase();
    const type = (i.type || "").toString().toLowerCase();
    // Exclude presentation-only titles from My Library tabs
    if (category === "presentation") return false;
    // Show items whose type matches the current tab (his / emb / three_d_model)
    return type === tabCategory;
  });

  const referencedForTab = (libraryData.referenced || []).filter((i) => {
    const type = (i.type || "").toString().toLowerCase();
    return type === tabCategory;
  });

  // Map of public_title_id -> array of slide_ids that are actually linked
  // into any private subtitle (via subtitle.referencedSlides).
  const linkedSlideIdsByPublicTitle = useMemo(() => {
    const map = {};
    (libraryData.private || []).forEach((pt) => {
      (pt.subtitles || []).forEach((sub) => {
        (sub.referencedSlides || []).forEach((ref) => {
          const publicTitleId = ref?.public_title_id;
          const slideId = ref?.id;
          if (!publicTitleId || !slideId) return;
          if (!map[publicTitleId]) map[publicTitleId] = new Set();
          map[publicTitleId].add(slideId);
        });
      });
    });
    const result = {};
    Object.keys(map).forEach((titleId) => {
      result[titleId] = Array.from(map[titleId]);
    });
    return result;
  }, [libraryData.private]);

  const handleAddReference = async (titleId) => {
    const res = await AddLibraryReferenceCall(titleId);
    if (res?.status === 201) {
      setMessage(t("pages.myLibrary.addedToLibrary"));
      await loadLibrary();
      setBrowseOpen(false);
    } else {
      setMessage(res?.message || t("pages.myLibrary.couldNotAdd"));
    }
  };

  const handleRemoveReference = async (titleId) => {
    if (!window.confirm(t("pages.myLibrary.removeFromLibraryConfirm"))) return;
    const res = await RemoveLibraryReferenceCall(titleId);
    if (res?.status === 200) {
      setMessage(t("pages.myLibrary.removedFromLibrary"));
      await loadLibrary();
    } else {
      setMessage(res?.message || t("pages.myLibrary.couldNotRemove"));
    }
  };

  const handleRemoveInlineRef = async (publicTitleId, slideId, targetSubtitleId) => {
    const res = await RemoveLibraryReferenceCall(publicTitleId, slideId, targetSubtitleId);
    if (res?.status === 200) {
      setMessage(t("pages.myLibrary.removedFromLibrary"));
      await loadLibrary();
    } else {
      setMessage(res?.message || t("pages.myLibrary.couldNotRemove"));
    }
  };

  const openAddFromLibrary = async (_subtitleId, item) => {
    // Link slides as separate subtitles under this private title.
    setTargetTitleId(item?.id || null);
    setTargetSubtitleId(null);
    setBrowseOpen(true);
    setSelectedSlides([]);
    setExpandedTitleIds({});
    setLoadingPublic(true);
    GetTitlesCallNoStore(lang, tabCategory).then((list) => {
      setPublicTitlesForTab(Array.isArray(list) ? list : []);
      setLoadingPublic(false);
    });
  };

  const currentVisibility = visibilityByCategory[tabCategory] || "none";

  const handleVisibilityChange = async (event) => {
    const value = event.target.value;
    setVisibilityByCategory((prev) => ({
      ...prev,
      [tabCategory]: value,
    }));
    await UpdateLibraryVisibilityCall({
      category: tabCategory,
      visibility: value,
    });
  };

  const getTitleLabel = (item) => {
    const locs = item?.titleLocalizations || [];
    const t = locs.find((l) => l.field === "title");
    return t?.value || item?.id || "Untitled";
  };

  const getSubtitleLabel = (subtitle) => {
    const locs = subtitle?.titleLocalizations || [];
    const t = locs.find((l) => l.field === "title");
    return t?.value || subtitle?.id || "Untitled";
  };

  const getSlideLabel = (slide, idx, subtitle = null) => {
    const locs = slide?.slideLocalizations || slide?.slide_localizations || [];
    const t = locs.find((l) => l.field === "title");
    if (t?.value && t.value.trim()) return t.value.trim();
    if (subtitle) return getSubtitleLabel(subtitle);
    return `Slide ${(idx ?? 0) + 1}`;
  };

  const getSlideThumbnailUrl = (slide) => {
    const url = slide?.Files?.[0]?.url;
    if (!url || typeof url !== "string") return null;
    return url.trim() ? `${url}/3/3/3.png` : null;
  };

  const refForTitle = (titleId) =>
    (libraryData.referenced || []).find((r) => r.id === titleId);

  const isSlideInLibrary = (item, slide) => {
    const ref = refForTitle(item.id);
    if (!ref) return false;
    return ref.referencedWholeTitle || (ref.referencedSlideIds || []).includes(slide.id);
  };

  const isSlideSelected = (titleId, slideId) =>
    selectedSlides.some((s) => s.titleId === titleId && s.slideId === slideId);

  const toggleSlideSelection = (titleId, slideId) => {
    setSelectedSlides((prev) => {
      const has = prev.some((s) => s.titleId === titleId && s.slideId === slideId);
      if (has) return prev.filter((s) => s.titleId !== titleId || s.slideId !== slideId);
      return [...prev, { titleId, slideId }];
    });
  };

  const toggleTitleExpanded = (titleId) => {
    setExpandedTitleIds((prev) => ({ ...prev, [titleId]: !prev[titleId] }));
  };

  const q = (browseSearchQuery || "").trim().toLowerCase();
  const filteredPublicTitlesForBrowse = useMemo(() => {
    if (!q) return publicTitlesForTab;
    return publicTitlesForTab
      .map((title) => {
        const titleLabel = getTitleLabel(title);
        const titleMatches = titleLabel.toLowerCase().includes(q);
        if (titleMatches) return title;
        const subtitles = (title.subtitles || [])
          .map((sub) => {
            const subLabel = getSubtitleLabel(sub);
            const subMatches = subLabel.toLowerCase().includes(q);
            const slides = (sub.slides || []).filter((slide, idx) => {
              const slideLabel = getSlideLabel(slide, idx, sub);
              return slideLabel.toLowerCase().includes(q);
            });
            const subHasMatch = subMatches || slides.length > 0;
            return subHasMatch ? { ...sub, slides: subMatches ? (sub.slides || []) : slides } : null;
          })
          .filter(Boolean);
        return subtitles.length > 0 ? { ...title, subtitles } : null;
      })
      .filter(Boolean);
  }, [publicTitlesForTab, browseSearchQuery]);

  const handleAddSelectedReferences = async () => {
    let added = 0;
    for (const { titleId, slideId } of selectedSlides) {
      const res = await AddLibraryReferenceCall(titleId, slideId, targetSubtitleId || undefined, targetTitleId || undefined);
      if (res?.status === 201) {
        added += 1;
        await loadLibrary();
      }
    }
    setMessage(added ? t("pages.myLibrary.addedSlidesToLibrary", { count: added }) : t("pages.myLibrary.nothingToAdd"));
    setSelectedSlides([]);
    setTargetSubtitleId(null);
    setTargetTitleId(null);
    setBrowseOpen(false);
  };

  const openReferencedTitle = (item) => {
    const t = item?.type;
    const firstSlide = item?.subtitles?.[0]?.slides?.[0] || item?.slides?.[0];
    const slideId = firstSlide?.id;
    if (!slideId) return;
    if (t === "three_d_model") {
      navigate(`/showThreeD/${slideId}/${lang}`);
    } else {
      navigate(`/showTitle/${slideId}/${lang}`);
    }
  };

  if (!authId) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={3} pb={3}>
        <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
          {t("pages.myLibrary.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("pages.myLibrary.description")}
        </Typography>
        {message && (
          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
            {message}
          </Typography>
        )}

        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          {TABS.map((tab, idx) => (
            <Tab
              key={tab.id}
              label={t(tab.labelKey)}
              id={`my-library-tab-${idx}`}
              aria-controls={`my-library-panel-${idx}`}
            />
          ))}
        </Tabs>

        {TABS.map((tab, idx) => (
          <div
            key={tab.id}
            role="tabpanel"
            hidden={tabIndex !== idx}
            id={`my-library-panel-${idx}`}
            aria-labelledby={`my-library-tab-${idx}`}
          >
            {tabIndex === idx && (
              <MDBox>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {`Your private ${t(tab.labelKey).toLowerCase()} — only your students can see these.`}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      Student view:
                    </Typography>
                    <TextField
                      select
                      value={currentVisibility}
                      onChange={handleVisibilityChange}
                      variant="outlined"
                      sx={{ minWidth: 200 }}
                      InputProps={{
                        sx: { minHeight: 44, py: 0.5 },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { minHeight: 120 },
                          },
                        },
                      }}
                    >
                      <MenuItem value="none">None (hide from students)</MenuItem>
                      <MenuItem value="all">All public slides</MenuItem>
                      <MenuItem value="private">
                        Only my private slides
                      </MenuItem>
                    </TextField>
                  </Box>
                </Box>
                <Titles
                  type={authId}
                  readOnly={false}
                  library_category={tab.id}
                  embedded
                  itemsFromLibrary={libraryData.private}
                  onRemoveLibraryRef={handleRemoveInlineRef}
                  onLibraryRefresh={loadLibrary}
                  addFromLibraryNode={(item) => (
                    <>
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        startIcon={<LinkIcon sx={{ fontSize: 18, opacity: 0.9 }} />}
                        onClick={() => openAddFromLibrary(item.subtitles?.[0]?.id, item)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 3,
                          px: 2,
                          py: 0.875,
                          letterSpacing: "0.02em",
                          borderWidth: 1.5,
                          "&:hover": {
                            borderWidth: 1.5,
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            boxShadow: theme.shadows[1],
                          },
                        }}
                      >
                        Add from library
                      </MDButton>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {item.subtitles?.[0] ? `Add slide into "${(item.subtitles[0].titleLocalizations?.find((l) => l.field === "title")?.value) || "this"}" (linked).` : "Add from library (a new subtitle will be created)."}
                      </Typography>
                    </>
                  )}
                />

                {false && referencedForTab.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: theme.palette.text.primary }}>
                      From public library
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {referencedForTab.map((item) => {
                        const explicitIds = linkedSlideIdsByPublicTitle[item.id] || item.referencedSlideIds || [];
                        const slideIdSet = new Set(explicitIds || []);
                        // If whole title is referenced OR we don't have explicit slide ids,
                        // fall back to showing all slides under each subtitle.
                        const includeAllSlides =
                          !!item.referencedWholeTitle || slideIdSet.size === 0;
                        const subtitles = item.subtitles || [];
                        return (
                          <Card key={item.id} variant="outlined">
                            <CardContent sx={{ py: 1.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1.25,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <LinkIcon color="action" />
                                  <Typography variant="body1">{getTitleLabel(item)}</Typography>
                                </Box>
                                <Box>
                                  <MDButton
                                    size="small"
                                    variant="text"
                                    onClick={() => openReferencedTitle(item)}
                                  >
                                    Open
                                  </MDButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveReference(item.id)}
                                    title="Remove from library"
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                              {subtitles.map((sub) => {
                                const allSlides = sub?.slides || [];
                                const slides = includeAllSlides
                                  ? allSlides
                                  : allSlides.filter((s) => slideIdSet.has(s.id));
                                if (!slides.length) return null;
                                return (
                                  <Box key={sub.id} sx={{ pl: 4, pb: 1.25 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.75 }}
                                    >
                                      {getSubtitleLabel(sub)}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.25,
                                      }}
                                    >
                                      {slides.map((slide, slideIdx) => {
                                        const label = getSlideLabel(slide, slideIdx, sub);
                                        const thumbUrl = getSlideThumbnailUrl(slide);
                                        return (
                                          <Box
                                            key={slide.id}
                                            sx={{
                                              borderRadius: 2,
                                              border: "1px solid",
                                              borderColor: "divider",
                                              overflow: "hidden",
                                              width: 120,
                                              flexShrink: 0,
                                              bgcolor: "background.paper",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                position: "relative",
                                                width: "100%",
                                                height: 70,
                                                overflow: "hidden",
                                                bgcolor: thumbUrl ? undefined : "rgba(0,0,0,0.04)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {thumbUrl ? (
                                                <Box
                                                  component="img"
                                                  src={thumbUrl}
                                                  alt={label}
                                                  sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    objectPosition: "center top",
                                                  }}
                                                />
                                              ) : (
                                                <Icon sx={{ fontSize: 24, color: "text.secondary" }}>photo_library</Icon>
                                              )}
                                            </Box>
                                            <Box sx={{ px: 0.75, py: 0.5 }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  display: "block",
                                                  fontWeight: 500,
                                                  lineHeight: 1.2,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {label}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </MDBox>
            )}
          </div>
        ))}
      </MDBox>

      <Dialog
        open={browseOpen}
        onClose={() => {
          setBrowseOpen(false);
          setBrowseSearchQuery("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add from public library — {currentTab?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select slides to add to your library. Expand each title to see subtitles and slides, then check the slides you want. Files are linked, not copied.
          </Typography>
          {!loadingPublic && publicTitlesForTab.length > 0 && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search titles, subtitles, slides…"
              value={browseSearchQuery}
              onChange={(e) => setBrowseSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Search public library" }}
            />
          )}
          {loadingPublic && <Typography variant="body2">Loading…</Typography>}
          {!loadingPublic && publicTitlesForTab.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {`No public ${currentTab ? t(currentTab.labelKey).toLowerCase() : ""} titles available.`}
            </Typography>
          )}
          {!loadingPublic && publicTitlesForTab.length > 0 && filteredPublicTitlesForBrowse.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No results for &quot;{browseSearchQuery.trim()}&quot;.
            </Typography>
          )}
          {!loadingPublic &&
            filteredPublicTitlesForBrowse.map((item) => {
              const expanded = !!expandedTitleIds[item.id];
              const subtitles = item?.subtitles || [];
              return (
                <Card key={item.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 0, px: 1.5, "&:last-child": { pb: 1 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 48,
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleTitleExpanded(item.id)}
                        sx={{ p: 0.5 }}
                        aria-label={expanded ? "Collapse" : "Expand"}
                      >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {getTitleLabel(item)}
                      </Typography>
                    </Box>
                    <Collapse in={expanded} timeout="auto">
                      <Box sx={{ pl: 2, pr: 1, pb: 1.5 }}>
                        {subtitles.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No subtitles
                          </Typography>
                        )}
                        {subtitles.map((sub) => {
                          const slides = sub?.slides || [];
                          return (
                            <Box key={sub.id} sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                                {getSubtitleLabel(sub)}
                              </Typography>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                                  gap: 1.5,
                                }}
                              >
                                {slides.length === 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    No slides
                                  </Typography>
                                )}
                                {slides.map((slide, slideIdx) => {
                                  const thumbUrl = getSlideThumbnailUrl(slide);
                                  const label = getSlideLabel(slide, slideIdx, sub);
                                  const subLabel = getSubtitleLabel(sub);
                                  const showLabel = label && label !== subLabel;
                                  const isDark = theme.palette.mode === "dark";
                                  const inLibrary = isSlideInLibrary(item, slide);
                                  const checked = inLibrary || isSlideSelected(item.id, slide.id);
                                  return (
                                    <Box
                                      key={slide.id}
                                      sx={{
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                        border: "1px solid",
                                        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                        boxShadow: 1,
                                        position: "relative",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 4,
                                          left: 4,
                                          zIndex: 1,
                                          bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.95)",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={inLibrary}
                                          onChange={() => !inLibrary && toggleSlideSelection(item.id, slide.id)}
                                          sx={{ p: 0.25 }}
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          position: "relative",
                                          width: "100%",
                                          paddingTop: "75%",
                                          overflow: "hidden",
                                          background: thumbUrl
                                            ? undefined
                                            : (isDark
                                                ? "linear-gradient(145deg, rgba(40,43,48,0.98) 0%, rgba(28,30,34,0.98) 100%)"
                                                : "linear-gradient(145deg, rgba(250,251,253,0.99) 0%, rgba(240,242,246,0.99) 100%)"),
                                        }}
                                      >
                                        {thumbUrl ? (
                                          <Box
                                            component="img"
                                            src={thumbUrl}
                                            alt={label || subLabel}
                                            sx={{
                                              position: "absolute",
                                              inset: 0,
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              objectPosition: "center",
                                            }}
                                          />
                                        ) : (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              inset: 0,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
                                            }}
                                          >
                                            <ImageIcon sx={{ fontSize: 28 }} />
                                          </Box>
                                        )}
                                      </Box>
                                      {showLabel && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            px: 0.75,
                                            py: 0.5,
                                            fontWeight: 500,
                                            lineHeight: 1.3,
                                            wordBreak: "break-word",
                                            minHeight: "2.6em",
                                          }}
                                        >
                                          {label}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <MDButton variant="text" onClick={() => setBrowseOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleAddSelectedReferences}
            disabled={selectedSlides.length === 0}
          >
            Add selected ({selectedSlides.length})
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default MyLibraryPage;
